// Firebase configuration
import firebase from 'firebase/compat/app';
import 'firebase/compat/auth';
import 'firebase/compat/firestore';
import 'firebase/compat/analytics';
import 'firebase/compat/functions';
import { getApp } from "firebase/app";
import {
  initializeAppCheck,
  ReCaptchaEnterpriseProvider,
  ReCaptchaV3Provider,
} from "firebase/app-check";
import { APP_CONFIG } from "./appConfig";

// Your web app's Firebase configuration
// For Firebase JS SDK v7.20.0 and later, measurementId is optional
const firebaseConfig = {
  apiKey: APP_CONFIG.firebaseApiKey,
  authDomain: APP_CONFIG.firebaseAuthDomain,
  projectId: APP_CONFIG.firebaseProjectId,
  storageBucket: APP_CONFIG.firebaseStorageBucket,
  messagingSenderId: APP_CONFIG.firebaseMessagingSenderId,
  appId: APP_CONFIG.firebaseAppId,
  measurementId: APP_CONFIG.firebaseMeasurementId
};

// Check if we have a valid Firebase config
const isFirebaseConfigured = Boolean(firebaseConfig.apiKey && firebaseConfig.projectId);

if (!isFirebaseConfigured && process.env.NODE_ENV === "production") {
  throw new Error(
    "Sparro: Firebase is not configured but production build was loaded. Set REACT_APP_FIREBASE_* in sparro/.env and rebuild."
  );
}

let app, auth, googleProvider, facebookProvider, appleProvider, db, cloudFunctions;

if (isFirebaseConfigured) {
  app = firebase.initializeApp(firebaseConfig);
  auth = firebase.auth();
  db = firebase.firestore();
  cloudFunctions = firebase.app().functions("europe-west1");

  if (typeof window !== "undefined" && APP_CONFIG.firebaseAppCheckSiteKey) {
    try {
      if (process.env.NODE_ENV !== "production") {
        window.FIREBASE_APPCHECK_DEBUG_TOKEN = true;
      }
      const modularApp = getApp();
      const Provider = APP_CONFIG.firebaseAppCheckUseV3
        ? ReCaptchaV3Provider
        : ReCaptchaEnterpriseProvider;
      initializeAppCheck(modularApp, {
        provider: new Provider(APP_CONFIG.firebaseAppCheckSiteKey),
        isTokenAutoRefreshEnabled: true,
      });
    } catch (e) {
      console.warn("App Check could not be initialized:", e?.message || e);
    }
  }

  if (typeof window !== "undefined" && firebase.analytics && APP_CONFIG.firebaseEnableAnalytics) {
    firebase.analytics();
  }

  googleProvider = new firebase.auth.GoogleAuthProvider();
  facebookProvider = new firebase.auth.FacebookAuthProvider();
  appleProvider = new firebase.auth.OAuthProvider('apple.com');
  appleProvider.addScope('email');
  appleProvider.addScope('name');
} else {
  console.warn('Firebase not configured. Using mock authentication. See FIREBASE_SETUP.md for setup instructions.');

  const MOCK_USER_STORAGE_KEY = "sparro_mock_user";
  const readPersistedMockUser = () => {
    if (typeof window === "undefined") return null;
    try {
      const raw = window.localStorage.getItem(MOCK_USER_STORAGE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== "object" || !parsed.uid) return null;
      return parsed;
    } catch {
      return null;
    }
  };
  const persistMockUser = (user) => {
    if (typeof window === "undefined") return;
    try {
      if (user) {
        window.localStorage.setItem(MOCK_USER_STORAGE_KEY, JSON.stringify({
          uid: user.uid,
          email: user.email || "",
          displayName: user.displayName || "",
        }));
      } else {
        window.localStorage.removeItem(MOCK_USER_STORAGE_KEY);
      }
    } catch {
      // Ignore storage errors in mock mode.
    }
  };

  let mockUser = null;
  const listeners = new Set();
  const notifyAuthChanged = () => {
    listeners.forEach((callback) => callback(mockUser));
  };
  const makeMockUser = (email, displayName = "") => ({
    uid: `mock-${(email || "user").toLowerCase().replace(/[^a-z0-9]/g, "") || "user"}`,
    email: email || "mock@example.com",
    displayName,
    updateProfile: async ({ displayName: nextName } = {}) => {
      mockUser = { ...mockUser, displayName: nextName || mockUser?.displayName || "" };
      persistMockUser(mockUser);
      notifyAuthChanged();
    },
  });

  const initialPersistedUser = readPersistedMockUser();
  if (initialPersistedUser?.uid) {
    mockUser = makeMockUser(initialPersistedUser.email, initialPersistedUser.displayName);
    mockUser = { ...mockUser, uid: initialPersistedUser.uid };
  }

  auth = {
    get currentUser() {
      return mockUser;
    },
    onAuthStateChanged: (callback) => {
      listeners.add(callback);
      callback(mockUser);
      return () => listeners.delete(callback);
    },
    signInWithPopup: async (provider) => {
      const providerId = provider?.providerId || "social";
      const email = `${providerId.replace(".com", "")}@mock.local`;
      mockUser = makeMockUser(email, providerId.replace(".com", ""));
      persistMockUser(mockUser);
      notifyAuthChanged();
      return { user: mockUser };
    },
    signInWithEmailAndPassword: async (email) => {
      mockUser = makeMockUser(email);
      persistMockUser(mockUser);
      notifyAuthChanged();
      return { user: mockUser };
    },
    createUserWithEmailAndPassword: async (email) => {
      mockUser = makeMockUser(email);
      persistMockUser(mockUser);
      notifyAuthChanged();
      return { user: mockUser };
    },
    signOut: async () => {
      mockUser = null;
      persistMockUser(null);
      notifyAuthChanged();
    },
    sendPasswordResetEmail: async () => {},
    confirmPasswordReset: async () => {},
  };
  
  googleProvider = { providerId: 'google.com' };
  facebookProvider = { providerId: 'facebook.com' };
  appleProvider = { providerId: 'apple.com' };
}

export { auth, googleProvider, facebookProvider, appleProvider, db, cloudFunctions };
export default app;
